import { Geo } from "@rogoag/geo";
import type { FeatureCollection } from "geojson";
import type { Job } from "../types";

/** MC's "Show stats" aggregates over a set of jobs (SchedulingMap.tsx displayStats), with
 *  two MC defects fixed: totals are not rounded before averaging (rounding is display-only)
 *  and jobs with an empty group value bucket under UNSET_GROUP_KEY instead of vanishing, so
 *  group rows sum to the top line. Averages keep MC's rule: divide by the count of fields
 *  with a nonzero value, so a field without data doesn't drag the average down. */
export interface JobAreaStats {
	fieldCount: number;
	totalBoundaryAcres: number;
	totalOpsAcres: number;
	totalSamples: number;
	avgAcresPerField: number;
	avgSamplesPerField: number;
}

/** MC's Group Statistics keys: Test Package / Grower / Client / SubmitterName. */
export type JobStatsGroupBy = "testPackage" | "growerName" | "companyName" | "submitterName";

export const UNSET_GROUP_KEY = "(unset)";

export interface JobGroupStats {
	key: string;
	stats: JobAreaStats;
}

// Keyed by the boundary object itself: a job's loaded boundary keeps its identity, and an edited
// or reloaded boundary is a new object, so it is measured afresh. Weak, so old boundaries go.
const acresByBoundary = new WeakMap<FeatureCollection, number>();

/**
 * A boundary's area in acres, measured once and remembered; malformed geometry counts as 0.
 * Measuring is the expensive part of every acre total — the lasso's live preview re-sums on each
 * animation frame, and the selection stats over thousands of jobs on each open.
 */
export function measuredBoundaryAcres(boundary: FeatureCollection): number {
	let acres = acresByBoundary.get(boundary);
	if (acres === undefined) {
		try {
			acres = Geo.area(boundary, "acres");
		} catch {
			acres = 0;
		}
		acresByBoundary.set(boundary, acres);
	}
	return acres;
}

/** Boundary acres for one job: measured from the loaded boundary geometry when present, else the
 *  "Boundary Acres" field. Malformed or empty geometry falls back to the field. The one per-job rule
 *  behind the selection pill's total (sumAcresForJobIds), the selection stats and the heatmap. */
export function jobBoundaryAcres(job: Job): number {
	const fc = job.files?.boundaryGeoJSON;
	const measured = fc ? measuredBoundaryAcres(fc) : 0;
	return measured > 0 ? measured : job.boundaryAcres || 0;
}

/** Resolve job ids to Job objects across the given pools (first pool wins on duplicates; ids
 *  missing from every pool are dropped). The Job-object counterpart of sumAcresForJobIds, with
 *  the same pool precedence, so the selection stats panel describes exactly what the acres
 *  summary counted. */
export function jobsForIds(ids: ReadonlySet<string>, ...pools: readonly (readonly Job[])[]): Job[] {
	const result: Job[] = [];
	const seen = new Set<string>();
	for (const pool of pools) {
		for (const job of pool) {
			if (!ids.has(job.id) || seen.has(job.id)) continue;
			seen.add(job.id);
			result.push(job);
		}
	}
	return result;
}

/** Total boundary acres (jobBoundaryAcres) for a set of job ids, searched across the given pools
 *  (e.g. scheduled then unscheduled). Each id is counted once even if it appears in more than one
 *  pool; a job whose geometry isn't loaded counts its "Boundary Acres" field, so the total matches
 *  the selection stats. Shared by the staged selection summary and the live lasso preview so both
 *  report acres the same way. */
export function sumAcresForJobIds(ids: ReadonlySet<string>, ...pools: readonly (readonly Job[])[]): number {
	let acres = 0;
	for (const job of jobsForIds(ids, ...pools)) acres += jobBoundaryAcres(job);
	return acres;
}

/** MC's shift-acres rule (DayShift.tsx, Drew 2025-09-29): "Ops Acre Eq." over "Boundary
 *  Acres", boundary as fallback — including when ops acres compute to 0 (MC's truthy check).
 *  Purely field-based (NOT jobBoundaryAcres, which is geometry-first): a shift card's total
 *  must equal MC's field-sum and not drift as boundary geometry loads in. */
export function jobOpsOrBoundaryAcres(job: Job): number {
	return job.opsAcres ? job.opsAcres : job.boundaryAcres || 0;
}

/** Shift-card total: ops-else-boundary acres summed over the shift's jobs, each counted once. */
export function sumOpsOrBoundaryAcres(jobs: readonly Job[]): number {
	let acres = 0;
	for (const job of dedupeById(jobs)) acres += jobOpsOrBoundaryAcres(job);
	return acres;
}

function dedupeById(jobs: readonly Job[]): Job[] {
	const seen = new Set<string>();
	const unique: Job[] = [];
	for (const job of jobs) {
		if (seen.has(job.id)) continue;
		seen.add(job.id);
		unique.push(job);
	}
	return unique;
}

export function summarizeJobs(jobs: readonly Job[]): JobAreaStats {
	const unique = dedupeById(jobs);
	let totalBoundaryAcres = 0;
	let totalOpsAcres = 0;
	let totalSamples = 0;
	let validAcresCount = 0;
	let validSamplesCount = 0;
	for (const job of unique) {
		const acres = jobBoundaryAcres(job);
		const samples = job.numberOfSamples ?? 0;
		totalBoundaryAcres += acres;
		totalOpsAcres += job.opsAcres ?? 0;
		totalSamples += samples;
		if (acres) validAcresCount++;
		if (samples) validSamplesCount++;
	}
	return {
		fieldCount: unique.length,
		totalBoundaryAcres,
		totalOpsAcres,
		totalSamples,
		avgAcresPerField: validAcresCount > 0 ? totalBoundaryAcres / validAcresCount : 0,
		avgSamplesPerField: validSamplesCount > 0 ? totalSamples / validSamplesCount : 0,
	};
}

export function groupJobStats(jobs: readonly Job[], groupBy: JobStatsGroupBy): JobGroupStats[] {
	const buckets = new Map<string, Job[]>();
	for (const job of dedupeById(jobs)) {
		const key = job[groupBy] || UNSET_GROUP_KEY;
		const bucket = buckets.get(key);
		if (bucket) bucket.push(job);
		else buckets.set(key, [job]);
	}
	const groups = [...buckets.entries()].map(([key, bucket]) => ({ key, stats: summarizeJobs(bucket) }));
	// First-seen order, with the unset bucket pinned last.
	return [...groups.filter((g) => g.key !== UNSET_GROUP_KEY), ...groups.filter((g) => g.key === UNSET_GROUP_KEY)];
}
